import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authClient } from './auth-client';
import './PasswordReset.css';

const MIN_PASSWORD_LENGTH = 8;

const PasswordReset = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const linkError = searchParams.get('error');

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(
    linkError ? 'This reset link is invalid or has expired. Please request a new one.' : ''
  );
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    const { error: apiError } = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (apiError) {
      setError(apiError.message || 'Failed to send password reset email');
    } else {
      setMessage("If an account exists for that email, we've sent a reset link. It expires in 1 hour.");
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long`);
      return;
    }

    setLoading(true);
    const { error: apiError } = await authClient.resetPassword({ newPassword, token });
    setLoading(false);

    if (apiError) {
      setError(
        apiError.code === 'INVALID_TOKEN'
          ? 'This reset link is invalid or has expired. Please request a new one.'
          : apiError.message || 'Failed to reset password'
      );
      return;
    }

    setDone(true);
    setMessage('Password reset successful! Redirecting you to sign in...');
    setTimeout(() => navigate('/login'), 2000);
  };

  const showResetForm = token && !linkError && !done;

  return (
    <div className="password-reset-container">
      <div className="password-reset-card">
        <h2>🔐 {showResetForm ? 'Choose a New Password' : 'Password Reset'}</h2>

        {showResetForm ? (
          <form onSubmit={handleResetPassword} className="password-reset-form">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                minLength={MIN_PASSWORD_LENGTH}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                minLength={MIN_PASSWORD_LENGTH}
                required
                disabled={loading}
              />
            </div>

            <button type="submit" className="reset-button" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        ) : !done && (
          <form onSubmit={handleRequestReset} className="password-reset-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                disabled={loading}
              />
              <small>We'll email you a link to choose a new password.</small>
            </div>

            <button type="submit" className="reset-button" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}

        <div className="form-links">
          <a href="/login">Back to Sign In</a>
          <a href="/register">Create New Account</a>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
